import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plug, Save, Trash2, Zap } from 'lucide-react';

import {
  integrationsApi,
  type Integration,
  type IntegrationProvider,
} from './api';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';

/**
 * Caso 13 — Outbound CRM integrations. v1 ships the webhook connector fully
 * working; HubSpot / Mailchimp are listed but disabled until OAuth apps are
 * registered (see CHECKLIST-INFRA.md §2.7). Until then the form refuses to
 * create those provider rows.
 */
export function IntegrationsPage() {
  const t = useT();
  useFavoritable(t('integrations.title'));
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Partial<Integration> | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['integrations', 'list'],
    queryFn: () => integrationsApi.list(),
  });

  const { data: stats } = useQuery({
    queryKey: ['integrations', 'stats'],
    queryFn: () => integrationsApi.stats(),
  });

  const saveMutation = useMutation({
    mutationFn: (row: Partial<Integration>) =>
      row.id
        ? integrationsApi.update(row.id, {
            provider: row.provider as IntegrationProvider,
            name: row.name,
            enabled: row.enabled,
            config: row.config,
          })
        : integrationsApi.create({
            provider: row.provider as IntegrationProvider,
            name: row.name,
            enabled: row.enabled,
            config: row.config,
          }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => integrationsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['integrations'] }),
  });

  const dispatchMutation = useMutation({
    mutationFn: () => integrationsApi.dispatchTick(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['integrations', 'stats'] }),
  });

  function blankFor(provider: IntegrationProvider): Partial<Integration> {
    if (provider === 'hubspot') {
      return {
        provider: 'hubspot',
        name: 'HubSpot',
        enabled: true,
        config: { accessToken: '', consentOnly: false },
      };
    }
    if (provider === 'mailchimp') {
      return {
        provider: 'mailchimp',
        name: 'Mailchimp',
        enabled: true,
        config: { apiKey: '', audienceId: '', consentOnly: false },
      };
    }
    return {
      provider: 'webhook',
      name: '',
      enabled: true,
      config: { url: '', secret: '', consentOnly: false },
    };
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-fg-primary)]">
            {t('integrations.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
            {t('integrations.subtitle')}
          </p>
        </div>
        {/* Caso 13 — provider picker. v2 ships Webhook + HubSpot +
            Mailchimp; the picker stays open via the small dropdown so
            users see all three from the start. */}
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button
            onClick={() => setEditing(blankFor('webhook'))}
            disabled={!!editing}
            variant="secondary"
          >
            <Plug className="h-4 w-4" />
            {t('integrations.new.webhook')}
          </Button>
          <Button
            onClick={() => setEditing(blankFor('hubspot'))}
            disabled={!!editing}
            variant="secondary"
          >
            <Plug className="h-4 w-4" />
            {t('integrations.new.hubspot')}
          </Button>
          <Button
            onClick={() => setEditing(blankFor('mailchimp'))}
            disabled={!!editing}
            variant="secondary"
          >
            <Plug className="h-4 w-4" />
            {t('integrations.new.mailchimp')}
          </Button>
        </div>
      </header>

      {/* Stats card — sync job pipeline */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>{t('integrations.stats.title')}</CardTitle>
            <CardDescription>{t('integrations.stats.subtitle')}</CardDescription>
          </div>
          <Button
            variant="ghost"
            onClick={() => dispatchMutation.mutate()}
            disabled={dispatchMutation.isPending}
          >
            <Zap className="h-4 w-4" />
            {dispatchMutation.isPending
              ? t('integrations.dispatch.running')
              : t('integrations.dispatch.run')}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(['PENDING', 'SENT', 'FAILED', 'SKIPPED'] as const).map((s) => (
              <div
                key={s}
                className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] p-3"
              >
                <div className="text-xs uppercase text-[var(--color-fg-muted)]">
                  {t(`integrations.state.${s}`)}
                </div>
                <div className="mt-1 text-xl font-semibold text-[var(--color-fg-primary)]">
                  {stats?.[s] ?? 0}
                </div>
              </div>
            ))}
          </div>
          {dispatchMutation.data ? (
            <p className="mt-3 text-xs text-[var(--color-fg-muted)]">
              {t('integrations.dispatch.lastRun')} —{' '}
              {t('integrations.state.SENT')}: {dispatchMutation.data.sent} ·{' '}
              {t('integrations.state.FAILED')}: {dispatchMutation.data.failed}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* List of configured integrations */}
      <Card>
        <CardHeader>
          <CardTitle>{t('integrations.list.title')}</CardTitle>
          <CardDescription>{t('integrations.list.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (data ?? []).length === 0 ? (
            <p className="text-sm text-[var(--color-fg-muted)]">
              {t('integrations.empty')}
            </p>
          ) : (
            <ul className="divide-y divide-[var(--color-border-default)]">
              {(data ?? []).map((row) => (
                <li
                  key={row.id}
                  className="flex items-start justify-between gap-3 py-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-[var(--color-fg-primary)]">
                        {row.name || row.provider}
                      </span>
                      <Badge tone={row.enabled ? 'success' : 'neutral'}>
                        {row.enabled
                          ? t('integrations.row.enabled')
                          : t('integrations.row.disabled')}
                      </Badge>
                      <Badge tone="neutral">{row.provider}</Badge>
                    </div>
                    {row.provider === 'webhook' &&
                    (row.config as { url?: string })?.url ? (
                      <p className="mt-1 truncate text-xs text-[var(--color-fg-muted)]">
                        {(row.config as { url?: string }).url}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => setEditing({ ...row })}
                    >
                      {t('common.edit')}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => deleteMutation.mutate(row.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-[var(--color-error)]" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Editor */}
      {editing ? (
        <IntegrationEditor
          row={editing}
          onChange={setEditing}
          onSave={() => saveMutation.mutate(editing)}
          onCancel={() => setEditing(null)}
          saving={saveMutation.isPending}
        />
      ) : null}
    </div>
  );
}

/**
 * Per-provider required-field gate. On edit, the secret can stay
 * "saved" (encrypted on disk) without breaking submit; on create the
 * secret is mandatory because there's nothing to fall back to.
 */
function canSave(
  provider: IntegrationProvider | undefined,
  cfg: Record<string, unknown>,
  isEdit: boolean,
): boolean {
  if (provider === 'webhook') {
    return !!cfg.url;
  }
  if (provider === 'hubspot') {
    const token = (cfg.accessToken as string | undefined) ?? '';
    return isEdit ? true : !!token;
  }
  if (provider === 'mailchimp') {
    const key = (cfg.apiKey as string | undefined) ?? '';
    const aud = (cfg.audienceId as string | undefined) ?? '';
    if (!aud) return false;
    return isEdit ? true : !!key;
  }
  return false;
}

function IntegrationEditor({
  row,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  row: Partial<Integration>;
  onChange: (next: Partial<Integration>) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const t = useT();
  // Generic config bag; provider-specific fields render below in their
  // own block.
  const cfg = (row.config ?? {}) as {
    // Webhook
    url?: string;
    secret?: string;
    // HubSpot
    accessToken?: string;
    // Mailchimp
    apiKey?: string;
    audienceId?: string;
    // Shared
    consentOnly?: boolean;
  };
  const isEdit = !!row.id;

  function patchCfg(patch: Partial<typeof cfg>) {
    onChange({ ...row, config: { ...cfg, ...patch } });
  }

  // Hint for whether the secret field appears empty because the user
  // hasn't typed it yet (new row) OR because the BE returns the
  // encrypted-on-disk value and the UI shows a placeholder to avoid
  // leaking it. v2: if `cfg.<secret>` starts with `enc:v1:`, render the
  // placeholder "saved" and let leave-empty-to-keep semantics rule.
  function isEncrypted(v?: string) {
    return !!v && v.startsWith('enc:v1:');
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {isEdit
            ? t('integrations.editor.editTitle')
            : t('integrations.editor.newTitle')}{' '}
          · {row.provider}
        </CardTitle>
        <CardDescription>
          {row.provider === 'webhook'
            ? t('integrations.editor.webhookHelp')
            : row.provider === 'hubspot'
            ? t('integrations.editor.hubspotHelp')
            : t('integrations.editor.mailchimpHelp')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-[var(--color-fg-muted)]">
            {t('integrations.editor.name')}
          </span>
          <input
            value={row.name ?? ''}
            onChange={(e) => onChange({ ...row, name: e.target.value })}
            className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-fg-primary)]"
            placeholder={t('integrations.editor.namePlaceholder')}
          />
        </label>

        {row.provider === 'webhook' && (
          <>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-[var(--color-fg-muted)]">
                {t('integrations.editor.url')}
              </span>
              <input
                value={cfg.url ?? ''}
                onChange={(e) => patchCfg({ url: e.target.value })}
                className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-fg-primary)]"
                placeholder="https://example.com/onxite-webhook"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-[var(--color-fg-muted)]">
                {t('integrations.editor.secret')}
              </span>
              <input
                value={isEncrypted(cfg.secret) ? '' : cfg.secret ?? ''}
                onChange={(e) => patchCfg({ secret: e.target.value })}
                type="password"
                autoComplete="new-password"
                className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-fg-primary)]"
                placeholder={
                  isEncrypted(cfg.secret)
                    ? t('integrations.editor.secretSaved')
                    : t('integrations.editor.secretPlaceholder')
                }
              />
              <span className="text-xs text-[var(--color-fg-muted)]">
                {t('integrations.editor.secretHelp')}
              </span>
            </label>
          </>
        )}

        {row.provider === 'hubspot' && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[var(--color-fg-muted)]">
              {t('integrations.editor.hubspotAccessToken')}
            </span>
            <input
              value={
                isEncrypted(cfg.accessToken) ? '' : cfg.accessToken ?? ''
              }
              onChange={(e) => patchCfg({ accessToken: e.target.value })}
              type="password"
              autoComplete="new-password"
              className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-fg-primary)]"
              placeholder={
                isEncrypted(cfg.accessToken)
                  ? t('integrations.editor.secretSaved')
                  : 'pat-na1-...'
              }
            />
            <span className="text-xs text-[var(--color-fg-muted)]">
              {t('integrations.editor.hubspotAccessTokenHelp')}
            </span>
          </label>
        )}

        {row.provider === 'mailchimp' && (
          <>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-[var(--color-fg-muted)]">
                {t('integrations.editor.mailchimpApiKey')}
              </span>
              <input
                value={isEncrypted(cfg.apiKey) ? '' : cfg.apiKey ?? ''}
                onChange={(e) => patchCfg({ apiKey: e.target.value })}
                type="password"
                autoComplete="new-password"
                className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-fg-primary)]"
                placeholder={
                  isEncrypted(cfg.apiKey)
                    ? t('integrations.editor.secretSaved')
                    : 'abcdef1234567890-us21'
                }
              />
              <span className="text-xs text-[var(--color-fg-muted)]">
                {t('integrations.editor.mailchimpApiKeyHelp')}
              </span>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-[var(--color-fg-muted)]">
                {t('integrations.editor.mailchimpAudienceId')}
              </span>
              <input
                value={cfg.audienceId ?? ''}
                onChange={(e) =>
                  patchCfg({ audienceId: e.target.value.trim() })
                }
                className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-fg-primary)]"
                placeholder="a1b2c3d4e5"
              />
              <span className="text-xs text-[var(--color-fg-muted)]">
                {t('integrations.editor.mailchimpAudienceIdHelp')}
              </span>
            </label>
          </>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!cfg.consentOnly}
            onChange={(e) =>
              onChange({
                ...row,
                config: { ...cfg, consentOnly: e.target.checked },
              })
            }
          />
          <span className="text-[var(--color-fg-primary)]">
            {t('integrations.editor.consentOnly')}
          </span>
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={row.enabled ?? true}
            onChange={(e) => onChange({ ...row, enabled: e.target.checked })}
          />
          <span className="text-[var(--color-fg-primary)]">
            {t('integrations.editor.enabled')}
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !canSave(row.provider, cfg, isEdit)}
          >
            <Save className="h-4 w-4" />
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
